import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MODULE_DEFS } from "../lib/modules";
import VoiceMic from "../components/VoiceMic";
import { ACCEPT_EXTENSIONS, CATEGORY_META, humanSize, PHOTO_SUBTYPE_OPTIONS } from "../lib/files";
import { uploadManyWithProgress } from "../lib/upload";
import UploadProgressList from "../components/UploadProgressList";
import UploadExtras from "../components/UploadExtras";
import { errMsg } from "../lib/errors";
import {
  Sparkles, ArrowRight, Loader2, Stethoscope, Upload, Trash2,
} from "lucide-react";

const INTAKE_FIELDS = [
  { name: "chief_complaint", label: "Patient chief complaint", type: "textarea", required: true },
  { name: "medical_history", label: "Medical history", type: "textarea" },
  { name: "dental_history", label: "Dental history", type: "textarea" },
  {
    name: "duration_untreated",
    label: "How long have the affected areas been untreated?",
    type: "text",
    placeholder: "e.g. 6 months, 2 years",
  },
  {
    name: "financial_priority",
    label: "Patient's financial considerations",
    type: "select",
    options: [
      { v: "no_preference", l: "No preference" },
      { v: "cost_conscious", l: "Cost-conscious" },
      { v: "best_outcome", l: "Wants best outcome regardless of cost" },
    ],
  },
  {
    name: "surgical_willingness",
    label: "Patient's surgical willingness",
    type: "select",
    options: [
      { v: "willing", l: "Willing for any indicated surgery" },
      { v: "reluctant", l: "Reluctant — prefers minimally invasive" },
      { v: "refuses", l: "Refuses surgical intervention" },
    ],
  },
  {
    name: "esthetic_vs_functional",
    label: "Esthetic vs functional priority",
    type: "select",
    options: [
      { v: "esthetic_first", l: "Esthetic first" },
      { v: "functional_first", l: "Functional first" },
      { v: "both_equal", l: "Both equally important" },
    ],
  },
  {
    name: "health_status",
    label: "Patient's overall health status (ASA)",
    type: "select",
    options: [
      { v: "ASA I", l: "ASA I" },
      { v: "ASA II", l: "ASA II" },
      { v: "ASA III", l: "ASA III" },
      { v: "ASA IV", l: "ASA IV" },
    ],
  },
];

export default function NewCaseAnalysis() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [concern, setConcern] = useState("");
  const [moduleSlug, setModuleSlug] = useState("");
  const [intake, setIntake] = useState({});
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [patientFiles, setPatientFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadItems, setUploadItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!patientId) {
      setPatientFiles([]);
      setSelectedFileIds([]);
      return;
    }
    api.get(`/patients/${patientId}/files`).then((r) => setPatientFiles(r.data));
  }, [patientId]);

  const setIntakeField = (k, v) => setIntake((p) => ({ ...p, [k]: v }));
  const toggleFile = (fid) =>
    setSelectedFileIds((p) => (p.includes(fid) ? p.filter((x) => x !== fid) : [...p, fid]));

  // Auto-create a "Quick Case" patient if the clinician hasn't linked one yet.
  // This unblocks inline upload — file storage in our schema is patient-scoped.
  const ensurePatientId = async () => {
    if (patientId) return patientId;
    const stamp = new Date().toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const r = await api.post("/patients", { name: `Quick Case — ${stamp}` });
    const newPid = r.data.id;
    setPatients((prev) => [r.data, ...prev]);
    setPatientId(newPid);
    return newPid;
  };

  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const pid = await ensurePatientId();
      const items = Array.from(fileList).map((f) => ({
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
      }));
      setUploadItems(
        items.map((it) => ({ id: it.id, name: it.name, pct: 0, status: "uploading" }))
      );
      const uploaded = [];
      await uploadManyWithProgress(`/patients/${pid}/files`, items, {
        concurrency: 3,
        onItemProgress: (id, pct) =>
          setUploadItems((prev) =>
            prev.map((it) => (it.id === id ? { ...it, pct } : it))
          ),
        onItemSuccess: (id, data) => {
          setUploadItems((prev) =>
            prev.map((it) =>
              it.id === id ? { ...it, pct: 100, status: "done" } : it
            )
          );
          uploaded.push(data);
        },
        onItemError: (id, error) => {
          setUploadItems((prev) =>
            prev.map((it) =>
              it.id === id ? { ...it, status: "error", error } : it
            )
          );
          toast.error(error);
        },
      });
      if (uploaded.length) {
        setPatientFiles((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          return [...uploaded.filter((u) => !seen.has(u.id)), ...prev];
        });
        setSelectedFileIds((prev) => {
          const seen = new Set(prev);
          return [...prev, ...uploaded.filter((u) => !seen.has(u.id)).map((u) => u.id)];
        });
        toast.success(`${uploaded.length} file(s) attached`);
      }
      setTimeout(() => setUploadItems([]), 1500);
    } catch (err) {
      toast.error(errMsg(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeUploadedFile = async (fid) => {
    try {
      await api.delete(`/files/${fid}`);
    } catch (_) {
      /* best-effort; still remove from local state so user can proceed */
    }
    setPatientFiles((prev) => prev.filter((x) => x.id !== fid));
    setSelectedFileIds((prev) => prev.filter((x) => x !== fid));
  };

  const setFileSubtype = async (fid, subtype) => {
    // Optimistic update
    setPatientFiles((prev) =>
      prev.map((f) =>
        f.id === fid
          ? {
              ...f,
              photo_subtype: subtype || null,
              category:
                subtype === "study_model_mounted" || subtype === "wax_up"
                  ? "study_model"
                  : subtype
                  ? "clinical_photo"
                  : f.category,
            }
          : f,
      ),
    );
    try {
      const r = await api.patch(`/files/${fid}`, { photo_subtype: subtype });
      // Reconcile with server canonical row.
      setPatientFiles((prev) => prev.map((f) => (f.id === fid ? r.data : f)));
    } catch (err) {
      toast.error(errMsg(err, "Could not update photo label"));
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const goToIntake = () => {
    if (concern.trim().length < 5) {
      toast.error("Please describe your presenting concern");
      return;
    }
    setStep(2);
  };

  const submit = async () => {
    if (!intake.chief_complaint?.trim()) {
      toast.error("Patient chief complaint is required");
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const r = await api.post("/case-analysis", {
        presenting_concern: concern,
        presenting_module_slug: moduleSlug || null,
        patient_id: patientId || null,
        file_ids: selectedFileIds,
        intake,
      });
      nav(`/case-analysis/${r.data.id}`);
    } catch (err) {
      toast.error(errMsg(err, "Analysis failed"));
      submittedRef.current = false;
      setSubmitting(false);
    }
  };

  // -------- Step 1: presenting concern --------
  if (step === 1) {
    return (
      <Layout>
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 kicker">
            <Sparkles size={12} className="text-[var(--pa-gold)]" /> ProsthoAI Complete Case Analysis
          </div>
          <h1
            className="font-serif text-3xl md:text-5xl text-[var(--pa-text)] mt-3 leading-[1.05]"
            data-testid="cca-title"
          >
            See the whole picture <span className="text-[var(--pa-gold)]">before treating the part.</span>
          </h1>
          <p className="mt-5 text-sm text-[var(--pa-text-muted)] leading-relaxed">
            Start by stating your specific clinical concern. ProsthoAI will then evaluate the
            patient as a whole — beginning with facial and occlusal analysis (facial proportions,
            lip support, vertical dimension, smile esthetics, and occlusion together) — before
            answering your question in the full context of the case.
          </p>

          <div className="gold-divider my-8" />

          <label className="kicker block mb-2">Your presenting concern</label>
          <div className="relative">
            <textarea
              rows={4}
              value={concern}
              onChange={(e) => setConcern(e.target.value)}
              placeholder='e.g. "I need to place an implant at #19. Tooth was extracted 8 months ago. What is the best plan?"'
              data-testid="cca-concern"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-4 pr-12"
            />
            <div className="absolute top-3 right-3">
              <VoiceMic value={concern} onChange={setConcern} testIdSuffix="cca-concern" />
            </div>
          </div>

          <div className="mt-4">
            <label className="kicker block mb-2">Most-relevant module (optional)</label>
            <select
              value={moduleSlug}
              onChange={(e) => setModuleSlug(e.target.value)}
              data-testid="cca-module"
              className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
            >
              <option value="">— Select if relevant —</option>
              {Object.entries(MODULE_DEFS).map(([s, d]) => (
                <option key={s} value={s}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={goToIntake}
            data-testid="cca-step1-next"
            className="mt-8 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            Continue to intake <ArrowRight size={16} />
          </button>
        </div>
      </Layout>
    );
  }

  // -------- Step 2: intake + imaging --------
  if (step === 2 && !submitting) {
    return (
      <Layout>
        <div className="max-w-4xl">
          <button
            onClick={() => setStep(1)}
            className="text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] mb-3"
          >
            ← Back
          </button>
          <div className="bg-[var(--pa-surface)] border border-[var(--pa-gold)]/50 rounded-lg p-5 mb-8">
            <div className="flex items-start gap-3">
              <Stethoscope size={20} className="text-[var(--pa-gold)] mt-0.5 shrink-0" />
              <div>
                <div className="kicker">ProsthoAI</div>
                <p className="text-sm text-[var(--pa-text)] mt-1.5 leading-relaxed">
                  Before we address your specific concern, ProsthoAI will first analyze the
                  complete clinical picture — starting with facial and occlusal analysis — to
                  ensure your treatment plan accounts for facial proportions, lip support,
                  vertical dimension, smile esthetics, and the entire dentition. Please upload
                  all available imaging and complete the patient profile.
                </p>
              </div>
            </div>
          </div>

          <div className="kicker">Patient intake</div>
          <h2 className="font-serif text-2xl md:text-3xl text-[var(--pa-text)] mt-1">
            Tell ProsthoAI about your patient
          </h2>

          <div className="gold-divider my-6" />

          {/* Patient picker */}
          <div className="grid sm:grid-cols-2 gap-5 mb-6">
            <div>
              <label className="kicker block mb-1.5">Link to patient record (optional)</label>
              <select
                value={patientId}
                onChange={(e) => setPatientId(e.target.value)}
                data-testid="cca-patient"
                className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
              >
                <option value="">— No patient linked —</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {patientId && patientFiles.length === 0 && (
            <div className="mb-6 text-xs text-[var(--pa-text-dim)] italic">
              No imaging uploaded for this patient yet — use the uploader below or{" "}
              <Link to={`/patients/${patientId}`} className="text-[var(--pa-gold)] underline">
                manage in the patient record
              </Link>
              .
            </div>
          )}

          {/* Intake form */}
          <div className="grid sm:grid-cols-2 gap-5">
            {INTAKE_FIELDS.map((f) => (
              <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <label className="block text-xs font-semibold text-[var(--pa-text-muted)] mb-1.5">
                  {f.label} {f.required && <span className="text-[var(--pa-gold)]">*</span>}
                </label>
                {f.type === "textarea" ? (
                  <div className="relative">
                    <textarea
                      rows={3}
                      value={intake[f.name] ?? ""}
                      onChange={(e) => setIntakeField(f.name, e.target.value)}
                      data-testid={`cca-intake-${f.name}`}
                      placeholder={f.placeholder}
                      className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5 pr-10"
                    />
                    <div className="absolute top-2 right-2">
                      <VoiceMic
                        value={intake[f.name] ?? ""}
                        onChange={(v) => setIntakeField(f.name, v)}
                        testIdSuffix={`cca-${f.name}`}
                      />
                    </div>
                  </div>
                ) : f.type === "select" ? (
                  <select
                    value={intake[f.name] ?? ""}
                    onChange={(e) => setIntakeField(f.name, e.target.value)}
                    data-testid={`cca-intake-${f.name}`}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
                  >
                    <option value="">Select…</option>
                    {f.options.map((o) => (
                      <option key={o.v} value={o.v}>
                        {o.l}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    value={intake[f.name] ?? ""}
                    onChange={(e) => setIntakeField(f.name, e.target.value)}
                    data-testid={`cca-intake-${f.name}`}
                    placeholder={f.placeholder}
                    className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm focus:border-[var(--pa-gold)] focus:outline-none p-2.5"
                  />
                )}
              </div>
            ))}
          </div>

          {/* ---------- Inline imaging upload ---------- */}
          <div className="mt-8" data-testid="cca-upload-section">
            <div className="kicker">Clinical photographs & study models</div>
            <h3 className="font-serif text-lg text-[var(--pa-text)] mt-1">
              Clinical Photographs &amp; Study Models
            </h3>
            <p className="text-xs text-[var(--pa-text-dim)] mt-1.5 leading-relaxed">
              Upload all available images for the most accurate analysis. More information
              leads to better diagnosis. Accepted: <span className="text-[var(--pa-text-muted)]">JPEG / PNG</span>{" "}
              from any source (iPhone, professional camera) — radiographs, full-face, profile,
              retracted intraoral, smile, occlusal arch, mounted study model, and diagnostic
              wax-up photographs. Also accepts CBCT DICOM, STL / PLY / OBJ scans, exocad exports,
              and Real Guide planning files (filename containing &ldquo;realguide&rdquo;).
            </p>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="cca-upload-dropzone"
              className={`mt-4 cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition ${
                dragOver
                  ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                  : "border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60 hover:bg-[var(--pa-bg-2)]/40"
              } ${uploading ? "pointer-events-none opacity-70" : ""}`}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_EXTENSIONS}
                onChange={(e) => uploadFiles(e.target.files)}
                data-testid="cca-upload-input"
                className="hidden"
              />
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-[var(--pa-text-muted)]">
                  <Loader2 size={16} className="animate-spin text-[var(--pa-gold)]" />
                  Uploading {uploadItems.filter((i) => i.status === "done").length} / {uploadItems.length}…
                </div>
              ) : (
                <>
                  <Upload size={22} className="text-[var(--pa-gold)] mx-auto" />
                  <p className="mt-2 text-sm text-[var(--pa-text)]">
                    <span className="text-[var(--pa-gold)] font-semibold">Click to select</span>{" "}
                    or drag & drop files
                  </p>
                  <p className="mt-1 text-[11px] text-[var(--pa-text-dim)]">
                    Radiographs • CBCT DICOM • STL / PLY / OBJ • exocad exports • up to 500 MB each
                  </p>
                </>
              )}
            </div>

            <UploadProgressList items={uploadItems} dataTestId="cca-upload-progress" />

            <UploadExtras
              patientId={patientId || null}
              onFiles={(files) => uploadFiles(files)}
              onFileImported={(rec) => {
                setPatientFiles((prev) =>
                  prev.find((x) => x.id === rec.id) ? prev : [rec, ...prev]
                );
                setSelectedFileIds((prev) =>
                  prev.includes(rec.id) ? prev : [...prev, rec.id]
                );
              }}
            />

            {patientFiles.length > 0 && (
              <div className="mt-4 space-y-1.5" data-testid="cca-uploaded-list">
                {patientFiles.map((f) => {
                  const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
                  const Icon = meta.Icon;
                  const checked = selectedFileIds.includes(f.id);
                  const isPhoto = /\.(jpe?g|png|webp)$/i.test(f.original_filename || "");
                  const vision = ["radiograph_2d", "clinical_photo", "study_model"].includes(
                    f.category,
                  );
                  const isRealguide = (f.category || "").startsWith("realguide_");
                  return (
                    <div
                      key={f.id}
                      data-testid={`cca-uploaded-${f.id}`}
                      className={`flex flex-wrap items-center gap-2.5 p-2.5 rounded-md border text-xs transition ${
                        checked
                          ? "border-[var(--pa-gold)]/60 bg-[var(--pa-gold)]/5"
                          : "border-[var(--pa-border)]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleFile(f.id)}
                        className="accent-[var(--pa-gold)]"
                        data-testid={`cca-uploaded-toggle-${f.id}`}
                      />
                      <Icon size={14} style={{ color: meta.color }} className="shrink-0" />
                      <div className="flex-1 min-w-0 min-w-[10rem]">
                        <div className="text-[var(--pa-text)] truncate">{f.original_filename}</div>
                        <div className="text-[10px] text-[var(--pa-text-dim)] mt-0.5">
                          {meta.label} · {humanSize(f.size)}
                          {vision && (
                            <span className="ml-2 text-[var(--pa-gold)]">→ AI vision</span>
                          )}
                          {isRealguide && (
                            <span className="ml-2 text-emerald-400">→ Real Guide verify</span>
                          )}
                        </div>
                      </div>
                      {isPhoto && (
                        <select
                          value={f.photo_subtype || ""}
                          onChange={(e) => setFileSubtype(f.id, e.target.value)}
                          data-testid={`cca-uploaded-subtype-${f.id}`}
                          className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded text-[10px] text-[var(--pa-text)] px-1.5 py-1 focus:border-[var(--pa-gold)] focus:outline-none max-w-[10rem]"
                          title="Label what this photograph depicts"
                        >
                          {PHOTO_SUBTYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                      <button
                        type="button"
                        onClick={() => removeUploadedFile(f.id)}
                        data-testid={`cca-uploaded-remove-${f.id}`}
                        className="p-1 rounded hover:bg-[var(--pa-bg-2)] text-[var(--pa-text-dim)] hover:text-red-400 transition"
                        title="Remove file"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* ---------- /Inline imaging upload ---------- */}

          <button
            onClick={submit}
            data-testid="cca-submit"
            disabled={uploading}
            className="mt-8 flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-7 py-3.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Run Complete Case Analysis <Sparkles size={16} />
          </button>
        </div>
      </Layout>
    );
  }

  // -------- Submitting: silent analysis screen --------
  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-16 text-center">
        <Sparkles size={36} className="text-[var(--pa-gold)] mx-auto" />
        <h1 className="font-serif text-3xl text-[var(--pa-text)] mt-6">
          ProsthoAI is analyzing the complete case…
        </h1>
        <p className="mt-4 text-sm text-[var(--pa-text-muted)] max-w-md mx-auto leading-relaxed">
          Beginning with facial and occlusal analysis. Working through all uploaded imaging,
          the intake, and the full case. This typically takes 15–30 seconds.
        </p>
        <div className="mt-8 inline-flex items-center gap-2 text-xs text-[var(--pa-text-dim)]">
          <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" />
          Reasoning through the case
        </div>
      </div>
    </Layout>
  );
}
